import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { store } from "../redux/store";
import { clearTokens, setTokens } from "../redux/slices/AuthSlice";
import { forceSessionLogout } from "../utils/sessionLogout";
import { refreshMutex } from "../auth/utils/refreshMutex";
import {
  callRefreshApi,
  callHeartbeatApi,
  clearAllTimers,
} from "../auth/services/tokenRefreshService";
import { updateSocketToken } from "../services/socket";
import { BACKEND_URL, serverURL, API_BASE_URL } from "../config/backend";
import { endpointGroupFromUrl, track, trackApiError } from "../analytics";

export { BACKEND_URL, serverURL, API_BASE_URL };

/** Request config with optional retry flag used by 401 interceptor */
interface InternalAxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function accessTokenFromConfig(config: InternalAxiosRequestConfig): string {
  const headers = config.headers as AxiosRequestHeaders | undefined;
  const raw = headers?.Authorization ?? headers?.authorization;
  if (typeof raw !== "string") return "";
  return raw.replace(/^Bearer\s+/i, "").trim();
}

class ApiClient {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  injectToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const state = store.getState();
    const token = state.auth.accessToken;

    // Ensure headers exist
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }

    // For FormData (e.g. document upload), let the browser set Content-Type with boundary
    if (config.data instanceof FormData) {
      delete (config.headers as AxiosRequestHeaders)["Content-Type"];
    }

    // Merge Authorization
    (config.headers as AxiosRequestHeaders).Authorization = token
      ? `Bearer ${token}`
      : "";

    return config;
  }

  initHttp() {
    const http = axios.create({
      baseURL: serverURL,
      headers: {},
    });

    // Request interceptor
    http.interceptors.request.use(this.injectToken, (error: unknown) =>
      Promise.reject(error)
    );

    // Response interceptor
    http.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as
          InternalAxiosRequestConfigWithRetry | undefined;

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          const state = store.getState();
          const refreshToken = state.auth.refreshToken;

          if (!refreshToken) {
            try {
              // Refresh impossible → forced logout (A.10). Enum reason only.
              track("session_refresh_failed", { reason: "no_refresh_token" });
            } catch {
              // fail-open
            }
            clearAllTimers();
            store.dispatch(clearTokens());
            window.location.href = "/";
            return Promise.reject(error);
          }

          try {
            const failedAccess = accessTokenFromConfig(originalRequest);
            const latest = store.getState().auth;
            const alreadyRotated =
              Boolean(latest.accessToken) &&
              Boolean(failedAccess) &&
              latest.accessToken !== failedAccess;

            const tokens = alreadyRotated
              ? {
                  accessToken: latest.accessToken as string,
                  refreshToken: latest.refreshToken as string,
                }
              : await refreshMutex.run(() => {
                  const rt = store.getState().auth.refreshToken;
                  if (!rt) {
                    throw new Error("No refresh token");
                  }
                  return callRefreshApi(rt);
                });
            if (!alreadyRotated) {
              store.dispatch(setTokens(tokens));
              updateSocketToken(tokens.accessToken);
            }
            void Promise.resolve(callHeartbeatApi(tokens.accessToken)).catch(
              () => undefined
            );
            return this.http(originalRequest);
          } catch (refreshError: unknown) {
            clearAllTimers();
            const responseData = (
              refreshError as AxiosError<{ message?: string; error?: string }>
            )?.response?.data;
            const message =
              responseData?.message || responseData?.error || "Session expired";

            // A.10: single source of truth for the failure class — drives both
            // the analytics enum (raw backend text never leaves) and the
            // logout branch, so they cannot drift (review MINOR).
            const refreshFailureReason = String(message)
              .toLowerCase()
              .includes("session idle timeout")
              ? "idle_timeout"
              : responseData?.message === "Refresh reuse detected"
                ? "refresh_reuse"
                : "expired";
            try {
              track("session_refresh_failed", { reason: refreshFailureReason });
            } catch {
              // fail-open
            }

            if (refreshFailureReason === "idle_timeout") {
              forceSessionLogout(message);
            } else {
              forceSessionLogout("Session expired. Please login again.");
            }
            window.location.href = "/";
            return Promise.reject(refreshError);
          }
        }

        try {
          const status = error.response?.status;
          const isFirstPass401 =
            status === 401 && !!originalRequest && !originalRequest._retry;
          // First-pass 401s route to the refresh/session flow above; every
          // other HTTP failure reaching the caller is a reliability signal
          // (A.15, rate-limited 1/60s in the helper). Network errors with no
          // response emit nothing. endpoint_group is derived — the URL and
          // query (PHI: search terms) never leave.
          if (status !== undefined && !isFirstPass401) {
            trackApiError({
              endpointGroup: endpointGroupFromUrl(originalRequest?.url),
              statusCode: status,
              method: (originalRequest?.method ?? "unknown").toUpperCase(),
            });
          }
        } catch {
          // fail-open: analytics must never alter the caller's rejection
        }
        return Promise.reject(error);
      }
    );

    this.instance = http;
    return http;
  }

  request<T = unknown, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.http.request(config);
  }

  get<T = unknown, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<Req = unknown, Res = unknown>(
    url: string,
    data?: Req,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Res>> {
    return this.http.post<Res, AxiosResponse<Res>, Req>(url, data, config);
  }

  put<Req = unknown, Res = unknown>(
    url: string,
    data?: Req,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Res>> {
    return this.http.put<Res, AxiosResponse<Res>, Req>(url, data, config);
  }

  patch<Req = unknown, Res = unknown>(
    url: string,
    data?: Req,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Res>> {
    return this.http.patch<Res, AxiosResponse<Res>, Req>(url, data, config);
  }

  delete<T = unknown, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }
}

export const apiClient = new ApiClient();
